import {
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useCommentDraft } from "@/hooks/useCommentDraft";
import { createPortal } from "react-dom";
import { Loader2, Paperclip, Reply, Send, Smile, Upload, X } from "lucide-react";
import { clsx } from "clsx";
import { fetchLinkPreview } from "../api/chatApi";
import { useUploadFile } from "../api/files";
import { uploadErrorMessage } from "@/utils/managedFile";
import type {
  ChatLinkPreview,
  ChatMessage,
  ChatMessageReplyBrief,
  ChatUserBrief,
} from "../types";
import { EmojiPicker } from "./EmojiPicker";

interface MessageComposerProps {
  roomId: string;
  onSend: (vars: {
    body: string;
    attachmentIds: string[];
    replyToId: string | null;
    linkPreviews: ChatLinkPreview[];
    mentionIds: string[];
  }) => Promise<void> | void;
  replyTo: ChatMessage | null;
  onClearReply: () => void;
  onAfterSend?: () => void;
  onTyping?: () => void;
  disabled?: boolean;
  members?: ChatUserBrief[];
}

interface PendingAttachment {
  id: string;
  filename: string;
  sizeBytes: number | null;
  contentType: string;
  url: string | null;
}

interface MentionDropdown {
  users: ChatUserBrief[];
  top: number;
  left: number;
  width: number;
  activeIdx: number;
}

const URL_RE = /\bhttps?:\/\/[^\s<>"']+/gi;
const MAX_ATTACHMENTS = 12;
const EVERYONE_ALIASES = ["wszyscy", "all"];
const EVERYONE_VIRTUAL: ChatUserBrief = {
  id: "__everyone__",
  full_name: "wszyscy",
  username: "__everyone__",
  profile_picture_url: null,
  is_external: false,
  company_name: null,
};

function humanSize(n: number | null): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function extractUrl(text: string): string | null {
  const m = text.match(URL_RE);
  return m ? m[0] : null;
}

function isImage(t: string): boolean {
  return !!t && t.startsWith("image/");
}

function hasEveryoneMention(text: string): boolean {
  return /@wszyscy\b/i.test(text) || /@all\b/i.test(text);
}

function getMentionedIds(text: string, members: ChatUserBrief[]): string[] {
  return members
    .filter((m) => text.includes(`@${m.full_name}`))
    .map((m) => m.id);
}

function getSubmitMentionIds(text: string, members: ChatUserBrief[]): string[] {
  if (hasEveryoneMention(text)) return members.map((m) => m.id);
  return getMentionedIds(text, members);
}

export function MessageComposer({
  roomId,
  onSend,
  replyTo,
  onClearReply,
  onAfterSend,
  onTyping,
  disabled,
  members = [],
}: MessageComposerProps) {
  const [text, setText, clearDraft] = useCommentDraft(`chat_draft_${roomId}`);
  const [busy, setBusy] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [preview, setPreview] = useState<ChatLinkPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewedUrl, setPreviewedUrl] = useState<string | null>(null);
  const [mentionDropdown, setMentionDropdown] = useState<MentionDropdown | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiContainerRef = useRef<HTMLDivElement>(null);
  const lastTypingAtRef = useRef<number>(0);
  const mentionDropdownRef = useRef(mentionDropdown);
  mentionDropdownRef.current = mentionDropdown;
  // Counter-based drag tracking avoids false dragleave when cursor moves
  // over a child element (browser fires leave on parent, then enter on child).
  const dragCounterRef = useRef(0);
  const upload = useUploadFile();

  const mentionedIds = getMentionedIds(text, members);

  function notifyTyping() {
    if (!onTyping) return;
    const now = Date.now();
    if (now - lastTypingAtRef.current < 2000) return;
    lastTypingAtRef.current = now;
    onTyping();
  }

  useEffect(() => {
    const url = extractUrl(text);
    if (!url) {
      if (preview) setPreview(null);
      setPreviewedUrl(null);
      return;
    }
    if (url === previewedUrl) return;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      const p = await fetchLinkPreview(url);
      setPreviewLoading(false);
      setPreview(p);
      setPreviewedUrl(url);
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  function detectMentionQuery(value: string, cursorPos: number): string | null {
    const before = value.slice(0, cursorPos);
    const m = before.match(/(@[^\s@]*)$/);
    return m ? m[1].slice(1) : null;
  }

  function handleTextChange(value: string, cursorPos: number) {
    setText(value);
    if (value.trim()) notifyTyping();

    if (!members.length) return;
    const query = detectMentionQuery(value, cursorPos);
    if (query !== null) {
      const showEveryone =
        members.length >= 2 &&
        EVERYONE_ALIASES.some((a) => a.startsWith(query.toLowerCase()));
      const filtered = members
        .filter((u) => u.full_name.toLowerCase().includes(query.toLowerCase()))
        .slice(0, showEveryone ? 7 : 8);
      const displayList: ChatUserBrief[] = [
        ...(showEveryone ? [EVERYONE_VIRTUAL] : []),
        ...filtered,
      ];
      if (displayList.length) {
        const el = textareaRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          setMentionDropdown({
            users: displayList,
            top: rect.top - 4,
            left: rect.left,
            width: rect.width,
            activeIdx: 0,
          });
        }
        return;
      }
    }
    setMentionDropdown(null);
  }

  function insertMention(user: ChatUserBrief) {
    const el = textareaRef.current;
    if (!el) return;
    const cursorPos = el.selectionStart ?? text.length;
    const before = text.slice(0, cursorPos);
    const after = text.slice(cursorPos);
    const m = before.match(/(@[^\s@]*)$/);
    if (!m) return;
    const newBefore = before.slice(0, before.length - m[0].length) + `@${user.full_name} `;
    const newText = newBefore + after;
    setText(newText);
    setMentionDropdown(null);
    requestAnimationFrame(() => {
      el.focus();
      const pos = newBefore.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function removeMention(userId: string) {
    const user = members.find((m) => m.id === userId);
    if (!user) return;
    const escaped = user.full_name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const next = text.replace(new RegExp(`@${escaped}`, "g"), user.full_name);
    setText(next);
  }

  function removeEveryoneMention() {
    const next = text.replace(/@wszyscy\b/gi, "wszyscy").replace(/@all\b/gi, "all");
    setText(next);
  }

  const isEveryoneMentioned = members.length >= 2 && hasEveryoneMention(text);

  async function submit() {
    const body = text.trim();
    if (!body && attachments.length === 0) return;
    if (busy) return;
    setBusy(true);
    const snapshot = { text, attachments, preview, previewedUrl };
    clearDraft();
    setAttachments([]);
    setPreview(null);
    setPreviewedUrl(null);
    setMentionDropdown(null);
    try {
      await onSend({
        body,
        attachmentIds: snapshot.attachments.map((a) => a.id),
        replyToId: replyTo?.id ?? null,
        linkPreviews: snapshot.preview ? [snapshot.preview] : [],
        mentionIds: getSubmitMentionIds(snapshot.text, members),
      });
      onClearReply();
      onAfterSend?.();
      requestAnimationFrame(() => onAfterSend?.());
      requestAnimationFrame(() => textareaRef.current?.focus());
    } catch {
      setText(snapshot.text);
      setAttachments(snapshot.attachments);
      setPreview(snapshot.preview);
      setPreviewedUrl(snapshot.previewedUrl);
    } finally {
      setBusy(false);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    const dropdown = mentionDropdownRef.current;

    if (dropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionDropdown((d) =>
          d ? { ...d, activeIdx: Math.min(d.activeIdx + 1, d.users.length - 1) } : d,
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionDropdown((d) =>
          d ? { ...d, activeIdx: Math.max(d.activeIdx - 1, 0) } : d,
        );
        return;
      }
      if (e.key === "Tab" || e.key === "Enter") {
        const user = dropdown.users[dropdown.activeIdx];
        if (user) {
          e.preventDefault();
          insertMention(user);
          return;
        }
      }
      if (e.key === "Escape") {
        setMentionDropdown(null);
        return;
      }
    }

    const composing =
      e.nativeEvent.isComposing ||
      (e.nativeEvent as unknown as { keyCode?: number }).keyCode === 229;
    if (e.key === "Enter" && !e.shiftKey && !composing) {
      e.preventDefault();
      void submit();
    }
    if (e.key === "Escape" && replyTo) {
      onClearReply();
    }
  }

  async function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    const files: File[] = [];
    for (const it of Array.from(items)) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      for (const f of files) await onPickFile(f);
    }
  }

  function insertEmoji(emoji: string) {
    const el = textareaRef.current;
    if (!el) {
      setText((t) => t + emoji);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  }

  async function onPickFile(file: File) {
    if (attachments.length >= MAX_ATTACHMENTS) {
      alert(`Maksymalnie ${MAX_ATTACHMENTS} załączników na wiadomość.`);
      return;
    }
    try {
      const mf = await upload.mutateAsync(file);
      setAttachments((prev) => [
        ...prev,
        {
          id: mf.id,
          filename: mf.original_filename,
          sizeBytes: mf.size_bytes,
          contentType: mf.content_type,
          url: mf.url,
        },
      ]);
    } catch (err) {
      alert(uploadErrorMessage(err, `Nie udało się wgrać pliku „${file.name}”.`));
    }
  }

  function removeAttachment(id: string) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  function onDragEnter(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }

  async function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    for (const f of files) await onPickFile(f);
  }

  useEffect(() => {
    if (!emojiOpen) return;
    function handler(e: MouseEvent) {
      if (
        emojiContainerRef.current &&
        !emojiContainerRef.current.contains(e.target as Node)
      ) {
        setEmojiOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [emojiOpen]);

  const replySender =
    replyTo?.sender?.full_name || replyTo?.sender?.username || "(nieznany)";
  const replyText: ChatMessageReplyBrief | null = replyTo
    ? {
        id: replyTo.id,
        sender: replyTo.sender,
        body: replyTo.body,
        attachments: replyTo.attachments,
        created_at: replyTo.created_at,
        deleted_at: replyTo.deleted_at,
      }
    : null;

  return (
    <div
      className={clsx(
        "border-t border-gray-200 bg-white relative",
        isDragOver && "border-red-400",
      )}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-white/90 border-2 border-dashed border-red-400 pointer-events-none">
          <Upload size={24} className="text-red-500" />
          <span className="text-sm font-medium text-red-600">Upuść pliki tutaj</span>
        </div>
      )}

      {replyText && (
        <div className="px-3 pt-2 pb-1 border-b border-gray-100 flex items-start gap-2 bg-gray-50">
          <Reply size={14} className="text-red-600 mt-0.5" />
          <div className="flex-1 min-w-0 border-l-2 border-red-600 pl-2">
            <div className="text-[11px] font-semibold text-red-700 truncate">
              Odpowiedź do: {replySender}
            </div>
            <div className="text-[12px] text-gray-700 truncate">
              {replyText.body ||
                (replyText.attachments.length > 0 ? "📎 Załącznik" : "")}
            </div>
          </div>
          <button
            type="button"
            onClick={onClearReply}
            title="Anuluj odpowiedź"
            className="text-gray-500 hover:text-gray-900"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {(preview || previewLoading) && (
        <div className="px-3 pt-2 pb-1 border-b border-gray-100 bg-gray-50 flex items-start gap-2">
          {previewLoading && !preview ? (
            <div className="text-[11px] text-gray-500 italic">
              Pobieram podgląd linku…
            </div>
          ) : preview ? (
            <div className="flex-1 min-w-0 flex gap-2">
              {preview.image && (
                <img
                  src={preview.image}
                  alt=""
                  className="w-10 h-10 rounded object-cover flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              <div className="flex-1 min-w-0">
                {preview.site_name && (
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider truncate">
                    {preview.site_name}
                  </div>
                )}
                <div className="text-[12px] font-semibold text-gray-900 truncate">
                  {preview.title || preview.url}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPreview(null);
                  setPreviewedUrl(null);
                }}
                title="Usuń podgląd"
                className="text-gray-500 hover:text-gray-900"
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="px-3 pt-2 pb-1 flex flex-wrap gap-2 border-b border-gray-100">
          {attachments.map((a) => (
            <div
              key={a.id}
              className="relative flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1 py-1 text-[11px] text-gray-700 group"
            >
              {a.contentType && isImage(a.contentType) && a.url ? (
                <img
                  src={a.url}
                  alt={a.filename}
                  className="w-12 h-12 rounded object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded bg-white border border-gray-200 flex items-center justify-center">
                  <Paperclip size={14} className="text-gray-500" />
                </div>
              )}
              <div className="flex flex-col min-w-0 max-w-[140px]">
                <span className="truncate" title={a.filename}>{a.filename}</span>
                <span className="text-gray-400">{humanSize(a.sizeBytes)}</span>
              </div>
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                title="Usuń załącznik"
                className="absolute -top-1.5 -right-1.5 bg-white border border-gray-300 rounded-full p-0.5 text-gray-600 hover:text-red-600 shadow-sm"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {(isEveryoneMentioned || mentionedIds.length > 0) && (
        <div className="px-3 pt-1.5 pb-1 flex flex-wrap gap-1 border-b border-gray-100 bg-gray-50">
          {isEveryoneMentioned ? (
            <button
              type="button"
              onClick={removeEveryoneMention}
              title="Kliknij, aby usunąć oznaczenie"
              className="text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full hover:bg-red-100 flex items-center gap-1"
            >
              @wszyscy <X size={10} />
            </button>
          ) : (
            mentionedIds.map((id) => {
              const u = members.find((m) => m.id === id);
              return u ? (
                <button
                  key={id}
                  type="button"
                  onClick={() => removeMention(id)}
                  title="Kliknij, aby usunąć oznaczenie"
                  className="text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full hover:bg-red-100 flex items-center gap-1"
                >
                  @{u.full_name} <X size={10} />
                </button>
              ) : null;
            })
          )}
        </div>
      )}

      <div className="p-2 sm:p-3 flex gap-2 items-end">
        <div className="flex flex-col gap-1">
          <div ref={emojiContainerRef} className="relative">
            {emojiOpen && (
              <div className="absolute bottom-full left-0 mb-1 z-30">
                <EmojiPicker onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />
              </div>
            )}
            <button
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              title="Emoji"
              className="p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600"
            >
              <Smile size={16} />
            </button>
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title="Załącz pliki (można wybrać wiele)"
            disabled={upload.isPending || attachments.length >= MAX_ATTACHMENTS}
            className="p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
          >
            {upload.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Paperclip size={16} />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={async (e) => {
              const files = Array.from(e.target.files ?? []);
              for (const f of files) await onPickFile(f);
              e.target.value = "";
            }}
          />
        </div>
        <textarea
          ref={textareaRef}
          rows={2}
          value={text}
          onChange={(e) => {
            handleTextChange(e.target.value, e.target.selectionStart);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          disabled={disabled}
          placeholder="Napisz wiadomość… (wpisz @ aby oznaczyć uczestnika)"
          className="flex-1 resize-none border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none disabled:opacity-60"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={
            disabled || busy || (!text.trim() && attachments.length === 0)
          }
          className="flex items-center gap-1.5 bg-red-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-40"
        >
          <Send size={14} /> Wyślij
        </button>
      </div>

      {mentionDropdown &&
        createPortal(
          <div
            style={{
              position: "fixed",
              bottom: `calc(100vh - ${mentionDropdown.top}px)`,
              left: mentionDropdown.left,
              width: mentionDropdown.width,
              zIndex: 9999,
              maxHeight: 224,
            }}
            className="bg-white border border-gray-200 shadow-lg overflow-y-auto"
            onMouseDown={(e) => e.preventDefault()}
          >
            {mentionDropdown.users.map((u, i) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={() => insertMention(u)}
                className={clsx(
                  "w-full text-left px-3 py-2 text-sm flex items-center gap-2",
                  i === mentionDropdown.activeIdx
                    ? "bg-red-50 text-red-700"
                    : "hover:bg-gray-50",
                )}
              >
                {u.id === "__everyone__" ? (
                  <>
                    <span className="w-6 h-6 flex-shrink-0 bg-red-600 text-white text-xs font-bold flex items-center justify-center rounded-full">
                      *
                    </span>
                    <span className="font-medium text-gray-800 leading-tight">
                      wszyscy{" "}
                      <span className="text-gray-400 text-xs font-normal">
                        — oznacz wszystkich
                      </span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="w-6 h-6 flex-shrink-0 bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center rounded-full">
                      {u.full_name.charAt(0)}
                    </span>
                    <span className="font-medium text-gray-800 leading-tight">
                      {u.full_name}
                    </span>
                  </>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
